import { Children } from 'react'

import { classNames } from '@/Ship/Helpers/classNames'

import type { ReactNode } from 'react'

import styles from './EntityCard.module.css'

export interface FieldSectionProps {
    title: string
    /** Необов'язкова. У блоках картки іконок не ставимо — див. `EntityLayout` */
    icon?: ReactNode
    children: ReactNode
    /** Блок, якому нема чого показати, не малюється зовсім */
    hidden?: boolean
    /** Лічильник або дія праворуч від заголовка */
    extra?: ReactNode
}

/**
 * Блок картки — біла плашка з жирним заголовком на сірому тлі.
 *
 * Межа між групами полів має бути видна з першого погляду: окремі плашки з
 * проміжком, а не підзаголовки з тонкою лінією всередині однієї картки.
 */
export const FieldSection = ({ title, icon, children, hidden = false, extra }: FieldSectionProps) => {
    if (hidden) return null

    return (
        <section className={classNames(styles['section'])}>
            <h3 className={classNames(styles['sectionTitle'])}>
                {icon ? <span className={classNames(styles['sectionIcon'])}>{icon}</span> : null}
                {title}
                {extra ? <span className={classNames(styles['sectionExtra'])}>{extra}</span> : null}
            </h3>
            {children}
        </section>
    )
}

/** Порожній стан блоку: чому тут нічого немає або коли з'явиться. */
export const SectionEmpty = ({ children }: { children: ReactNode }) => (
    <div className={classNames(styles['sectionEmpty'])}>{children}</div>
)

/**
 * Поля блоку двома стовпцями; на вузькому блоці — одним. `adaptive` — для
 * картки без бокової стрічки: на широкому блоці три, далі чотири стовпці,
 * щоб поля не розтягувались на пів екрана (картка заявки).
 *
 * Поле, яке в цьому стані не показується, не лишає діри: порожня комірка
 * випадає з сітки, і наступне поле стає на її місце.
 */
export const FieldGrid = ({ children, adaptive = false }: { children: ReactNode; adaptive?: boolean }) => (
    <div className={classNames(styles['grid'], adaptive && styles['gridAdaptive'])}>{children}</div>
)

export interface FieldCellProps {
    /** На всю ширину блоку: назва, опис, файли */
    wide?: boolean
    children: ReactNode
}

export const FieldCell = ({ wide = false, children }: FieldCellProps) => {
    if (Children.toArray(children).length === 0) return null

    return <div className={classNames(wide && styles['wide'])}>{children}</div>
}
